<template>
    <div class="product-show-container py-4">
        <!-- Breadcrumb & Top Bar -->
        <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <router-link to="/home" key="back-to-home-link" class="btn-back-link d-inline-flex align-items-center text-decoration-none">
                <div class="icon-circle mr-2">
                    <i class="fas fa-arrow-left"></i>
                </div>
                <span class="font-weight-bold">Voltar aos Produtos</span>
            </router-link>

            <!-- Breadcrumbs -->
            <nav aria-label="breadcrumb" class="d-none d-md-block" key="breadcrumbs-nav">
                <ol class="breadcrumb custom-breadcrumb mb-0 py-1 px-3 rounded-pill">
                    <li class="breadcrumb-item">
                        <router-link to="/home"><i class="fas fa-home mr-1"></i> Início</router-link>
                    </li>
                    <li class="breadcrumb-item active" aria-current="page" v-if="dados.categoria" key="bread-cat">
                        {{ dados.categoria }}
                    </li>
                    <li class="breadcrumb-item active text-truncate" style="max-width: 200px;" aria-current="page" key="bread-name">
                        {{ dados.nome }}
                    </li>
                </ol>
            </nav>
        </div>

        <div class="row align-items-start">
            <!-- Left Column: Product Gallery -->
            <div class="col-xl-7 col-lg-6 mb-5">
                <div class="gallery-wrapper rounded-3xl p-3 shadow-sm border-theme bg-surface-theme">
                    <!-- Featured Main Image Display -->
                    <div class="main-image-box position-relative rounded-2xl overflow-hidden mb-3">
                        <img 
                            v-if="imagemPrincipalUrl"
                            key="img-main-has"
                            :src="imagemPrincipalUrl"
                            class="main-img w-100 zoomable-image" 
                            :alt="dados.nome"
                            @click="abrirModalImagem(imagemPrincipalUrl)">
                        <div v-else key="img-main-none" class="no-image-box d-flex flex-column justify-content-center align-items-center py-5">
                             <i class="fas fa-image fa-4x mb-3 text-secondary-theme opacity-50"></i>
                             <span class="font-weight-bold text-secondary-theme">Sem Imagem Cadastrada</span>
                        </div>

                        <!-- Zoom Indicator Badge -->
                        <div v-if="imagemPrincipalUrl" key="zoom-badge-btn" class="zoom-badge position-absolute" @click="abrirModalImagem(imagemPrincipalUrl)" title="Clique para ampliar">
                            <i class="fas fa-search-plus mr-1"></i> Ampliar
                        </div>

                        <!-- Image Counter Badge -->
                        <div v-if="arquivos && arquivos.length > 1" key="counter-badge-box" class="counter-badge position-absolute">
                            {{ fotoAtivaIndex + 1 }} / {{ arquivos.length }}
                        </div>
                    </div>

                    <!-- Thumbnails Strip / Carousel -->
                    <div v-if="arquivos && arquivos.length > 1" key="thumbs-strip" class="thumbnails-grid d-flex align-items-center gap-2 overflow-x-auto pb-2">
                        <div 
                            v-for="(arquivo, index) in arquivos" 
                            :key="'thumb-' + arquivo.id"
                            class="thumb-item rounded-xl overflow-hidden"
                            :class="{ 'active': fotoAtivaIndex === index }"
                            @click="selecionarFoto(index, $url+'/api/produtos/image/'+arquivo.id)">
                            <img 
                                :src="$url+'/api/produtos/image/'+arquivo.id" 
                                class="thumb-img w-100 h-100" 
                                :alt="dados.nome + ' foto ' + (index+1)">
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Column: Product Info & Purchase Action -->
            <div class="col-xl-5 col-lg-6 mb-5 pl-lg-4">
                <div class="product-info-panel p-4 p-md-5 rounded-3xl bg-surface-theme border-theme shadow-sm">
                    <!-- Category Badge -->
                    <div class="mb-3" v-if="dados.categoria" key="category-badge-box">
                        <span class="badge badge-pill badge-category-detail px-3 py-2">
                            <i class="fas fa-tag mr-1 text-accent"></i> {{ dados.categoria }}
                        </span>
                    </div>

                    <!-- Product Title -->
                    <h1 class="product-title-detail font-weight-bold mb-4">
                        {{ dados.nome }}
                    </h1>

                    <!-- Price Card -->
                    <div class="price-box p-4 rounded-2xl mb-4 position-relative overflow-hidden">
                        <div class="d-flex align-items-baseline">
                            <span class="currency-symbol mr-1">R$</span>
                            <span class="price-value">{{ dados.valor_br }}</span>
                        </div>

                        <div class="mt-3 pt-3 border-top-light d-flex align-items-center text-success small font-weight-bold">
                            <i class="fas fa-check-circle mr-2"></i> Produto disponível para encomenda
                        </div>
                    </div>

                    <!-- Primary WhatsApp Interest Button -->
                    <div class="cta-actions mb-4">
                        <button class="btn btn-lg w-100 text-white font-weight-bold shadow-md whatsapp-btn d-flex align-items-center justify-content-center py-3 mb-3" @click="abrirWhatsapp">
                            <i class="fab fa-whatsapp fa-lg mr-2 pulse-icon"></i> Tenho Interesse no WhatsApp
                        </button>

                        <div class="d-flex gap-2">
                            <button class="btn btn-secondary-action flex-grow-1 d-flex align-items-center justify-content-center py-2" @click="copiarLink">
                                <i class="fas fa-link mr-2"></i> {{ linkCopiado ? 'Link Copiado!' : 'Copiar Link' }}
                            </button>
                        </div>
                    </div>

                    <!-- Highlights List -->
                    <div class="highlights-box p-3 rounded-xl">
                        <div class="d-flex align-items-center mb-2 text-secondary-theme small">
                            <i class="fas fa-truck mr-2 text-accent"></i> <span>Envio e entrega combinados via WhatsApp</span>
                        </div>
                        <div class="d-flex align-items-center text-secondary-theme small">
                            <i class="fas fa-shield-alt mr-2 text-accent"></i> <span>Atendimento personalizado e direto</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Product Description & Specs Section -->
        <div class="row mt-2 mb-5">
            <div class="col-12">
                <div class="description-card p-4 p-md-5 rounded-3xl bg-surface-theme border-theme shadow-sm">
                    <h4 class="section-heading font-weight-bold mb-4 d-inline-flex align-items-center">
                        <span class="icon-box-accent mr-3">
                            <i class="fas fa-align-left"></i>
                        </span>
                        <span>Detalhes e Descrição</span>
                    </h4>

                    <div 
                        v-if="dados.descricao_site"
                        key="desc-content-has"
                        class="text-primary-theme description-content" 
                        v-html="dados.descricao_site">
                    </div>
                    <div v-else key="desc-content-none" class="text-secondary-theme py-3 italic">
                        Nenhuma descrição detalhada informada para este produto.
                    </div>
                </div>
            </div>
        </div>

        <!-- Image Zoom Modal Overlay -->
        <div 
            v-if="imagemModalUrl" 
            key="modal-zoom-overlay"
            class="image-modal-overlay d-flex align-items-center justify-content-center" 
            @click.self="fecharModal"
            @wheel.prevent="handleWheel">
            <button class="btn-close-modal" @click="fecharModal" title="Fechar (Esc)">
                <i class="fas fa-times"></i>
            </button>
            <div class="zoom-controls position-absolute d-flex align-items-center rounded-pill px-3 py-2 shadow-lg">
                <button class="btn btn-sm text-white px-2" @click="zoomOut" title="Reduzir"><i class="fas fa-search-minus"></i></button>
                <span class="small font-weight-bold mx-3 text-white">{{ Math.round(zoomScale * 100) }}%</span>
                <button class="btn btn-sm text-white px-2" @click="zoomIn" title="Ampliar"><i class="fas fa-search-plus"></i></button>
                <button class="btn btn-sm text-white-50 ml-2 pl-2 border-left" @click="resetZoom" title="Resetar"><i class="fas fa-sync-alt"></i></button>
            </div>
            <div class="image-modal-content text-center position-relative overflow-hidden">
                <img 
                    :src="imagemModalUrl" 
                    class="img-fluid rounded modal-zoom-img" 
                    :style="{ transform: `scale(${zoomScale})` }"
                    :alt="dados.nome">
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        data(){
            return {
                dados: {},
                arquivos: [],
                arquivo_principal: {},
                imagemPrincipalUrl: null,
                fotoAtivaIndex: 0,
                imagemModalUrl: null,
                zoomScale: 1,
                linkCopiado: false
            }
        },
        created(){
            window.scrollTo(0, 0);
            this.getDados();
        },
        mounted(){
            window.scrollTo(0, 0);
        },
        watch: {
            '$route.params.id': function(newId) {
                if (newId) {
                    window.scrollTo(0, 0);
                    this.getDados();
                }
            }
        },
        methods: {
            getDados(){
                axios.get(this.$url+'/api/produtos/show/'+this.$route.params.id)
	            .then(response => {
	                if (!response.data || response.data.type === 'error' || !response.data.id) {
	                    this.$router.push('/home');
	                    return;
	                }
	            	this.dados = response.data
                    this.arquivos = response.data.arquivos || []
                    this.arquivo_principal = response.data.arquivo_principal || {}
                    
                    if (this.arquivos.length > 0) {
                        this.imagemPrincipalUrl = this.$url + '/api/produtos/image/' + this.arquivos[0].id;
                        this.fotoAtivaIndex = 0;
                    } else if (this.arquivo_principal && this.arquivo_principal.id) {
                        this.imagemPrincipalUrl = this.$url + '/api/produtos/image/' + this.arquivo_principal.id;
                        this.fotoAtivaIndex = 0;
                    } else {
                        this.imagemPrincipalUrl = null;
                    }
	            })
	            .catch(error => {
	            	console.log(error);
	            	this.$router.push('/home');
	            });
            },
            selecionarFoto(index, url) {
                this.fotoAtivaIndex = index;
                this.imagemPrincipalUrl = url;
            },
            abrirModalImagem(url) {
                if (!url) return;
                this.imagemModalUrl = url;
                this.zoomScale = 1;
            },
            fecharModal() {
                this.imagemModalUrl = null;
                this.zoomScale = 1;
            },
            handleWheel(event) {
                if (event.deltaY < 0) {
                    this.zoomIn();
                } else {
                    this.zoomOut();
                }
            },
            zoomIn() {
                if (this.zoomScale < 3.5) {
                    this.zoomScale = Math.min(3.5, this.zoomScale + 0.25);
                }
            },
            zoomOut() {
                if (this.zoomScale > 0.5) {
                    this.zoomScale = Math.max(0.5, this.zoomScale - 0.25);
                }
            },
            resetZoom() {
                this.zoomScale = 1;
            },
            abrirWhatsapp() {
                const numero = '5571999434836';
                const mensagem = `Tenho interesse no produto ${this.dados.nome}`;
                const url = `https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`;
                window.open(url, '_blank');
            },
            copiarLink() {
                navigator.clipboard.writeText(window.location.href).then(() => {
                    this.linkCopiado = true;
                    setTimeout(() => {
                        this.linkCopiado = false;
                    }, 2500);
                });
            }
        }
    }
</script>

<style scoped>
    .product-show-container {
        max-width: 1240px;
        margin: 0 auto;
    }

    /* Back Button & Breadcrumbs */
    .btn-back-link {
        color: var(--text-primary);
        transition: all 0.25s ease;
    }

    .btn-back-link:hover {
        color: var(--accent-color);
        transform: translateX(-4px);
    }

    .icon-circle {
        width: 38px;
        height: 38px;
        border-radius: 50%;
        background-color: var(--surface-color);
        border: 1px solid var(--border-color);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
    }

    .custom-breadcrumb {
        background-color: var(--surface-color);
        border: 1px solid var(--border-color);
        font-size: 0.88rem;
    }

    .custom-breadcrumb a {
        color: var(--text-secondary);
        text-decoration: none;
        transition: color 0.2s ease;
    }

    .custom-breadcrumb a:hover {
        color: var(--accent-color);
    }

    /* Gallery Styling */
    .gallery-wrapper {
        border-radius: 24px !important;
    }

    .main-image-box {
        height: 480px;
        background-color: var(--bg-color);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .main-img {
        height: 100%;
        object-fit: cover;
        cursor: zoom-in;
        transition: transform 0.4s ease;
    }

    .main-img:hover {
        transform: scale(1.02);
    }

    .zoom-badge {
        top: 15px;
        right: 15px;
        background: rgba(15, 23, 42, 0.65);
        color: #ffffff;
        font-size: 0.78rem;
        font-weight: 600;
        padding: 6px 14px;
        border-radius: 30px;
        backdrop-filter: blur(6px);
        cursor: pointer;
        transition: background 0.2s ease;
    }

    .zoom-badge:hover {
        background: rgba(15, 23, 42, 0.85);
    }

    .counter-badge {
        bottom: 15px;
        right: 15px;
        background: rgba(0, 0, 0, 0.6);
        color: #ffffff;
        font-size: 0.75rem;
        font-weight: 700;
        padding: 4px 12px;
        border-radius: 20px;
        backdrop-filter: blur(4px);
    }

    /* Thumbnails */
    .thumbnails-grid {
        gap: 10px;
    }

    .thumb-item {
        width: 80px;
        height: 80px;
        flex-shrink: 0;
        cursor: pointer;
        border: 2px solid transparent;
        transition: all 0.25s ease;
        opacity: 0.65;
        background-color: var(--bg-color);
        border-radius: 14px !important;
    }

    .thumb-item:hover {
        opacity: 1;
    }

    .thumb-item.active {
        border-color: var(--accent-color);
        opacity: 1;
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(145, 216, 247, 0.4);
    }

    .thumb-img {
        object-fit: cover;
    }

    /* Product Info Panel */
    .product-info-panel {
        border-radius: 24px !important;
    }

    .badge-category-detail {
        background-color: var(--bg-color);
        color: var(--text-primary);
        border: 1px solid var(--border-color);
        font-size: 0.85rem;
        font-weight: 600;
    }

    .product-title-detail {
        font-size: 2.2rem;
        color: var(--text-primary);
        letter-spacing: -0.8px;
        line-height: 1.2;
    }

    /* Price Box */
    .price-box {
        background: linear-gradient(135deg, rgba(40, 167, 69, 0.08) 0%, rgba(40, 167, 69, 0.03) 100%);
        border: 1px solid rgba(40, 167, 69, 0.2);
    }

    .price-label-detail {
        font-size: 0.72rem;
        letter-spacing: 0.8px;
        font-weight: 700;
        color: #28a745;
    }

    .currency-symbol {
        font-size: 1.2rem;
        font-weight: 700;
        color: #28a745;
    }

    .price-value {
        font-size: 2.5rem;
        font-weight: 800;
        color: #28a745;
        letter-spacing: -1px;
        line-height: 1;
    }

    .border-top-light {
        border-top: 1px solid rgba(40, 167, 69, 0.15);
    }

    /* Buttons */
    .whatsapp-btn {
        background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
        border: none;
        border-radius: 50px;
        font-size: 1.1rem;
        transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    }
    
    .whatsapp-btn:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 25px rgba(37, 211, 102, 0.4) !important;
        background: linear-gradient(135deg, #28e16d 0%, #107c6f 100%);
    }

    .pulse-icon {
        animation: pulse 2s infinite;
    }

    @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.2); }
        100% { transform: scale(1); }
    }

    .btn-secondary-action {
        background-color: var(--bg-color);
        color: var(--text-primary);
        border: 1px solid var(--border-color);
        border-radius: 50px;
        font-weight: 600;
        font-size: 0.9rem;
        transition: all 0.25s ease;
    }

    .btn-secondary-action:hover {
        border-color: var(--accent-color);
        color: var(--accent-color);
    }

    .highlights-box {
        background-color: var(--bg-color);
        border: 1px solid var(--border-color);
    }

    /* Description Section */
    .description-card {
        border-radius: 24px !important;
    }

    .icon-box-accent {
        width: 42px;
        height: 42px;
        border-radius: 12px;
        background-color: rgba(145, 216, 247, 0.2);
        color: var(--accent-color);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 1.1rem;
    }

    .section-heading {
        color: var(--text-primary);
        font-size: 1.4rem;
        letter-spacing: -0.4px;
    }

    .description-content {
        line-height: 1.8;
        font-size: 1.08rem;
    }

    .description-content >>> * {
        color: var(--text-primary) !important;
        background-color: transparent !important;
    }

    .description-content >>> p {
        margin-bottom: 1.2rem;
    }

    /* Modal Zoom Overlay */
    .image-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background-color: rgba(15, 23, 42, 0.88);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        z-index: 2000;
        animation: fadeIn 0.25s ease;
        padding: 20px;
    }

    .btn-close-modal {
        position: absolute;
        top: 25px;
        right: 30px;
        background: rgba(255, 255, 255, 0.15);
        border: none;
        color: #ffffff;
        width: 48px;
        height: 48px;
        border-radius: 50%;
        font-size: 1.4rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        z-index: 2010;
    }

    .btn-close-modal:hover {
        background: rgba(255, 255, 255, 0.3);
        transform: scale(1.1);
    }

    .zoom-controls {
        bottom: 30px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 2010;
        background-color: rgba(15, 23, 42, 0.85) !important;
        border: 1px solid rgba(255, 255, 255, 0.15);
        backdrop-filter: blur(8px);
    }

    .modal-zoom-img {
        max-width: 85vw;
        max-height: 80vh;
        object-fit: contain;
        box-shadow: 0 25px 50px rgba(0, 0, 0, 0.6);
        transition: transform 0.15s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        user-select: none;
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    @media (max-width: 768px) {
        .main-image-box {
            height: 320px;
        }
        .product-title-detail {
            font-size: 1.7rem;
        }
        .price-value {
            font-size: 2rem;
        }
    }
</style>